import { ChiptaElement, html, css } from 'chipta-libs/main.js';

class CountryFlag extends ChiptaElement {

  /**
   * Get the flag files from this repo:
   * https://hampusborgos.github.io/country-flags/
   *
   * Generally use SVG, but some SVGs are very large and then the PNG is more efficient.
   *
   * This component is meant to be flexible scalable. So the flags should scale
   * to the container they're in.
   *
   * To make the SVGs scalable set these attributes:
   *   preserveAspectRatio="none" width="100%" height="100%"
   *
   * The PNGs are scaled by the CSS class `flag-bg-image`. You only need to add
   * an extra CSS class to set the `background-image` property:
   *   background-image: url("data:image/png;base64,BASE64STRING==");
   */

  static get properties() {
    return {
      countryCode: {type: String}
    };
  }

  render() {
    return html`<div id="flag">${this.flagIcon}</div>`;
  }

  get flagIcon() {

    const iconFuncName = 'flag_' + this.countryCode;

    if (iconFuncName in this) {
      return this[iconFuncName];
    }

  }

  get flag_en() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" preserveAspectRatio="none" width="100%" height="100%">
        <path fill="#012169" d="M0 0h640v480H0z"/>
        <path fill="#FFF" d="M75 0l244 181L562 0h78v62L400 241l240 178v61h-80L320 301 81 480H0v-60l239-178L0 64V0h75z"/>
        <path fill="#C8102E" d="M424 281l216 159v40L369 281h55zm-184 20l6 35L54 480H0l240-179zM640 0v3L391 191l2-44L590 0h50zM0 0l239 176h-60L0 42V0z"/>
        <path fill="#FFF" d="M241 0v480h160V0H241zM0 160v160h640V160H0z"/>
        <path fill="#C8102E" d="M0 193v96h640v-96H0zM273 0v480h96V0h-96z"/>
      </svg>
    `;
  }

  get flag_nl() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 480" preserveAspectRatio="none" width="100%" height="100%">
        <path fill="#21468B" d="M0 0h640v480H0z"/>
        <path fill="#FFF" d="M0 0h640v320H0z"/>
        <path fill="#AE1C28" d="M0 0h640v160H0z"/>
      </svg>
    `;
  }


  get flag_be() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 450 390" preserveAspectRatio="none" width="100%" height="100%">
        <rect fill="#ED2939" width="450" height="390"/>
        <rect fill="#FAE042" width="300" height="390"/>
        <rect width="150" height="390"/>
      </svg>
    `;
  }

  get flag_es() {
    return html`<span class="flag-bg-image flag-es"></span>`;
  }

  get flag_europe() {
    return html`
      <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 810 540" preserveAspectRatio="none" width="100%" height="100%">
        <desc>European flag</desc>
        <defs>
          <g id="s">
            <g id="c">
              <path id="t" d="M0,0v1h0.5z" transform="translate(0,-1)rotate(18)"/>
              <use xlink:href="#t" transform="scale(-1,1)"/>
            </g>
            <g id="a">
              <use xlink:href="#c" transform="rotate(72)"/>
              <use xlink:href="#c" transform="rotate(144)"/>
            </g>
            <use xlink:href="#a" transform="scale(-1,1)"/>
          </g>
        </defs>
        <rect fill="#039" width="810" height="540"/>
        <g fill="#fc0" transform="scale(30)translate(13.5,9)">
          <use xlink:href="#s" y="-6"/>
          <use xlink:href="#s" y="6"/>
          <g id="l">
            <use xlink:href="#s" x="-6"/>
            <use xlink:href="#s" transform="rotate(150)translate(0,6)rotate(66)"/>
            <use xlink:href="#s" transform="rotate(120)translate(0,6)rotate(24)"/>
            <use xlink:href="#s" transform="rotate(60)translate(0,6)rotate(12)"/>
            <use xlink:href="#s" transform="rotate(30)translate(0,6)rotate(42)"/>
          </g>
          <use xlink:href="#l" transform="scale(-1,1)"/>
        </g>
      </svg>
    `;
  }

  static get styles() {

    return css`

      :host {
        display: block;
      }

      #flag {
        display: flex;
        width: 100%;
        height: 100%;
      }

      .flag-bg-image {
        display: inline-block;
        width: 100%;
        height: 100%;
        background-size: cover;
      }

      .flag-es {
        background-image: url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABsAAAAUCAYAAAB8gkaAAAAABmJLR0QA/wD/AP+gvaeTAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAB3RJTUUH4gEWCx8RdPXwVwAAAB1pVFh0Q29tbWVudAAAAAAAQ3JlYXRlZCB3aXRoIEdJTVBkLmUHAAABmUlEQVRIx+2VP0tcURDFf3Pffe66D3XFPI1ZUWGNYBEwtsEEYqoFq9S2foRUgUC+QgqLgLXaWdlI2E0RBbGLQRNdgjGruBpJ1P373r3WYrRYH1uIpxyGOZyZOTOylRq2NAmKJuLukmmdlJszBIhoqlJffnJtKSVQqhq8mCKMgFCr2NWgsbBbrJP/WSO3HpB55vLAd3mccjHmFmT/C9aNJffhjP3dAElYvqzUCUfgzftuKJtoFkQEDooBH+cPGZ/02NYVTjt9is+HeT09xNziISenYXTK3NYYQd8k/xay/JWAzNgrvhcc9koteL1JUAWgFo2yr9kSsl/l+CTA7/T4/G2H1GiaSjnB77VzCts1lBOBMgv0djgc5fc4ehmSWnfJFf7gz77FfRon3TZAu2uxViIwtQXpV5RfDOIAvheS6coTKo0NBWeiB92tG/bdlTZmNzVLyxukxxIcJztY1X20Dj0kM9XGu5lf/DgwiDRo6uDTZVM7yoICawRRUKlCPGYxoaC0xQaCaVCZbKhHTXsxWsWlaYf4/p9FggtoUIs/HFxC/gAAAABJRU5ErkJggg==");
      }

    `;

  }

}

customElements.define('country-flag', CountryFlag);
